import React, { useState } from "react";
import { useNavigate } from "react-router-dom"; // at top

import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink, Github, List, LayoutGrid, Search } from "lucide-react";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";

export default function ProjectsShowcase({ projects }) {
  const [search, setSearch] = useState("");
  const [view, setView] = useState("grid");
  const [searchOpen, setSearchOpen] = useState(false);
  const [showAll] = useState(false);
  const navigate = useNavigate();

  const featured = projects.filter((p) => p.featured === true);
  const filteredProjects = projects.filter(
    (p) =>
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.desc.toLowerCase().includes(search.toLowerCase()) ||
      p.tech.some((t) => t.toLowerCase().includes(search.toLowerCase()))
  );

  const projectsToShow = showAll
    ? filteredProjects
    : filteredProjects.slice(0, 4);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      {/* FEATURED SECTION */}
      <div className="flex flex-col">
        {/* Small metallic "Featured" */}
        <span className="text-sm text-gray-400 dark:text-gray-500 tracking-wide uppercase">
          Featured
        </span>

        {/* Main "Projects" */}
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          Projects
        </h2>
      </div>

      {/* HORIZONTAL SCROLL FEATURED */}
      <motion.div
        className="mt-6 flex gap-6 overflow-x-auto snap-x snap-mandatory pb-3 no-scrollbar sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {featured.map((proj, i) => (
          <motion.div
            key={proj.title}
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.15 }}
            className="min-w-[250px] sm:min-w-0 snap-center rounded-xl shadow-md dark:bg-slate-900 bg-white border dark:border-gray-700 overflow-hidden hover:scale-[1.03] transition duration-300"
          >
            <img src={proj.image} alt="" className="h-40 w-full object-cover" />
            <div className="p-4">
              <h3 className="text-lg font-semibold dark:text-white">
                {proj.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">
                {proj.desc}
              </p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Search Icon & Input */}
      <div className="flex items-center justify-between mt-3 w-full gap-3">
        {/* SEARCH */}
        <div className="relative flex-1">
          
          <motion.div
            initial={{ width: 40 }}
            animate={{ width: searchOpen ? "80%" : 40 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="flex items-center relative border dark:border-gray-700 rounded-full overflow-hidden dark:bg-slate-900 bg-white cursor-pointer"
            onClick={() => setSearchOpen(!searchOpen)}
          >
            <Tippy content="Search" placement="bottom">
              <Search className="text-gray-400 mx-3 my-2" size={20} />
            </Tippy>
            <AnimatePresence>
              {searchOpen && (
                <motion.input
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  type="text"
                  autoFocus
                  className="w-full pl-2 pr-4 py-2 dark:bg-slate-900 bg-white dark:text-white text-gray-800 outline-none"
                  placeholder="Search projects..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              )}
            </AnimatePresence>
          </motion.div>
          
        </div>

        {/* VIEW TOGGLE */}
        <div className="flex gap-2 flex-shrink-0">
          <Tippy content="Grid View" placement="bottom">
            <button
              className={`p-2 rounded-full border transition flex items-center justify-center ${
                view === "grid"
                  ? "bg-black text-white dark:bg-white dark:text-black"
                  : "dark:text-white hover:bg-gray-200 dark:hover:bg-gray-800"
              }`}
              onClick={() => setView("grid")}
            >
              <LayoutGrid size={20} />
            </button>
          </Tippy>

          <Tippy content="List View" placement="bottom">
            <button
              className={`p-2 rounded-full border transition flex items-center justify-center ${
                view === "list"
                  ? "bg-black text-white dark:bg-white dark:text-black"
                  : "dark:text-white hover:bg-gray-200 dark:hover:bg-gray-800"
              }`}
              onClick={() => setView("list")}
            >
              <List size={20} />
            </button>
          </Tippy>
        </div>
      </div>

      {/* PROJECT LIST */}
      <div
        className={`mt-12 ${
          view === "grid"
            ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6"
            : "flex flex-col gap-6"
        }`}
      >
        {projectsToShow.map((proj, i) => (
          <motion.div
            key={proj.title}
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="relative border rounded-xl overflow-hidden bg-white dark:bg-slate-900 dark:border-gray-700 shadow-sm hover:shadow-xl transition hover:-translate-y-1 duration-300"
          >
            {/* STATUS BADGE */}
            <Tippy
              content={
                proj.status === "working" ? "Currently Coding" : "Project Live"
              }
            >
              <span className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-md bg-white/20 dark:bg-gray-800/30 border border-white/30 dark:border-gray-600/30 flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 animate-glow ${
                    proj.status === "working" ? "bg-red-500" : "bg-green-400"
                  }`}
                />
                {proj.status === "working" ? "Building" : "Working"}
              </span>
            </Tippy>

            <img
              src={proj.image}
              alt={proj.title}
              className={`${
                view === "list"
                  ? "h-40 w-full object-cover"
                  : "h-56 w-full object-cover"
              }`}
            />

            <div className="p-5">
              {/* TITLE + LIVE / GITHUB ICONS */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold dark:text-white">
                  {proj.title}
                </h3>

                <div className="flex gap-2">
                  {proj.live && (
                    <Tippy content="View Website">
                      <a
                        href={proj.live}
                        rel="noreferrer"
                        target="_blank"
                        className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                      >
                        <ExternalLink size={18} />
                      </a>
                    </Tippy>
                  )}
                  {proj.code && (
                    <Tippy content="View Github">
                      <a
                        href={proj.code}
                        rel="noreferrer"
                        target="_blank"
                        className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                      >
                        <Github size={18} />
                      </a>
                    </Tippy>
                  )}
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                {proj.desc}
              </p>

              {/* TECH STACK */}
              <div className="flex flex-wrap gap-2 mt-3">
                {proj.tech.map((t, idx) => (
                  <Tippy content={`Tech: ${t}`} key={idx}>
                    <span className="px-2 py-1 border rounded-md text-xs dark:text-gray-300 dark:border-gray-600">
                      {t}
                    </span>
                  </Tippy>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* VIEW MORE BUTTON */}

      <div className="flex justify-center mt-8">
        <button
          onClick={() => navigate("/view-all-projects")} // Navigate to your projects page
          className="px-6 py-2 rounded-lg bg-black text-white dark:bg-white dark:text-black hover:opacity-90 transition"
        >
          View All Projects
        </button>
      </div>
    </section>
  );
}
